import React, { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Paper,
} from "@mui/material";

// Helper that tolerates both `id`/`Id` casing coming back from Sequelize.
const getBrandId = (b) => b?.id ?? b?.Id;

// Map a grid row to the exact shape the backend (karigarorder) expects.
const rowToStone = (row) => ({
  type: row.type,
  description: row.description || "",
  stonepcs: row.stonepcs,
  weightPerPcs: row.weightPerPcs,
  weight: row.weight,
});

// A row is saved only when it has a Weight (> 0). Pcs / Wt-per-pc alone are
// not enough — so a stone with no weight is never saved.
const isRowFilled = (row) => parseFloat(row.weight) > 0;

/**
 * Brand-driven stone grid.
 *
 * Renders a Brand dropdown. When the chosen brand resolves to >= 1 real stone
 * (via `Stone_id_list`), it shows an editable grid instead of the one-at-a-time
 * "Add Stone" form and drives the parent's stoneList. Otherwise it stays
 * inactive and the parent keeps its normal manual flow untouched.
 *
 * Props:
 *  - brands:     storeData.brand
 *  - stones:     storeData.stone
 *  - value:      { brandId, stoneList } for the current QR entry
 *  - entryKey:   changes when the parent switches QR entries (rehydrate trigger)
 *  - onChange:   ({ brandId, stoneList, active }) => void
 */
const BrandStoneGrid = ({
  brands = [],
  stones = [],
  value = {},
  entryKey,
  onChange,
}) => {
  const [brandId, setBrandId] = useState(value.brandId || "");
  const [rows, setRows] = useState([]);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Remembers which stone types the karigar marked "Other" so the marking
  // carries forward to the next SL.No (Add Next) even though values reset.
  const otherTypesRef = useRef(new Set());

  // Build the grid rows for a brand, pre-filling from an existing stoneList.
  const buildRows = (bId, prefillList = []) => {
    const brand = brands.find((b) => String(getBrandId(b)) === String(bId));
    if (!brand || !brand.Stone_id_list) return [];

    const ids = String(brand.Stone_id_list)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    return (
      ids
        .map((idStr) => {
          // Stone_id_list refers to stone_master.Quick_no, NOT the primary id.
          // Compare NUMERICALLY: Quick_no is decimal(6,1), so the DB returns an
          // integer like 4 as "4.0" — a string compare ("4.0" !== "4") would
          // miss it. Numeric compare also matches decimals like 2.5 correctly.
          const target = Number(idStr);
          const stone = Number.isNaN(target)
            ? null
            : stones.find(
                (s) => s?.Quick_no != null && Number(s.Quick_no) === target,
              );
          if (!stone) return null;
          const name = stone.Stone_name;
          const existing = prefillList.find((e) => e.type === name);
          // A loaded entry's description wins; otherwise carry a remembered
          // "Other" marking forward for this stone type.
          const description = existing
            ? (existing.description ?? "")
            : otherTypesRef.current.has(name)
              ? "Other"
              : "";
          return {
            stoneId: idStr,
            quickNo: Number(stone.Quick_no),
            type: name,
            stonepcs: existing?.stonepcs ?? "",
            weightPerPcs: existing?.weightPerPcs ?? "",
            weight: existing?.weight ?? "",
            description,
          };
        })
        .filter(Boolean)
        // Rows are ordered by the stone's Quick_no (not the Stone_id_list order).
        .sort((a, b) => a.quickNo - b.quickNo)
    );
  };

  // `source` lets the parent tell a user action apart from a rehydrate, so it
  // never clears a freshly loaded manual stoneList when the grid is inactive.
  const emit = (bId, nextRows, source) => {
    const active = !!bId && nextRows.length > 0;
    const stoneList = active
      ? nextRows.filter(isRowFilled).map(rowToStone)
      : [];
    onChangeRef.current?.({ brandId: bId, stoneList, active, source });
  };

  // Rehydrate whenever the parent switches to a different QR entry.
  useEffect(() => {
    const bId = value.brandId || "";
    const nextRows = bId ? buildRows(bId, value.stoneList || []) : [];
    setBrandId(bId);
    setRows(nextRows);
    emit(bId, nextRows, "init");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryKey, brands.length, stones.length]);

  const handleBrandChange = (e) => {
    const bId = e.target.value;
    const nextRows = buildRows(bId, value.stoneList || []);
    setBrandId(bId);
    setRows(nextRows);
    emit(bId, nextRows, "user");
  };

  const handleCellChange = (index, field, raw) => {
    setRows((prev) => {
      const next = prev.map((r) => ({ ...r }));
      const row = next[index];
      row[field] = raw;

      // Track "Other" markings per stone type so they persist across Add Next.
      if (field === "description") {
        if ((raw || "").trim().toLowerCase() === "other") {
          otherTypesRef.current.add(row.type);
        } else {
          otherTypesRef.current.delete(row.type);
        }
      }

      // Weight auto-calculates from pcs * weight-per-pc (like the manual form).
      if (field === "stonepcs" || field === "weightPerPcs") {
        const pcs = parseFloat(row.stonepcs) || 0;
        const perPc = parseFloat(row.weightPerPcs) || 0;
        row.weight = pcs && perPc ? (pcs * perPc).toFixed(3) : "";
      }

      // Direct weight entry: default Pcs to 1 so the row is complete without
      // the karigar having to type Pcs manually.
      if (field === "weight") {
        const hasWeight = raw !== "" && raw != null;
        const pcsEmpty = row.stonepcs === "" || row.stonepcs == null;
        if (hasWeight && pcsEmpty) {
          row.stonepcs = "1";
        }
      }

      emit(brandId, next, "user");
      return next;
    });
  };

  const gridActive = !!brandId && rows.length > 0;

  return (
    <Box sx={{ mt: 2 }}>
      <FormControl fullWidth sx={{ mb: gridActive ? 2 : 0 }}>
        <InputLabel id="brand-label">Brand</InputLabel>
        <Select
          labelId="brand-label"
          value={brandId}
          onChange={handleBrandChange}
          label="Brand"
          sx={{ textAlign: "left" }}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {brands.map((b) => (
            <MenuItem key={getBrandId(b)} value={getBrandId(b)}>
              {b.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {gridActive && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Stone</TableCell>
                <TableCell sx={{ width: 90 }}>Pcs</TableCell>
                <TableCell sx={{ width: 160 }}>Wt/Pc</TableCell>
                <TableCell sx={{ width: 160 }}>Weight</TableCell>
                <TableCell sx={{ minWidth: 180 }}>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow key={row.stoneId}>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                    {row.type}
                  </TableCell>
                  <TableCell sx={{ width: 90 }}>
                    <TextField
                      value={row.stonepcs}
                      onChange={(e) =>
                        handleCellChange(index, "stonepcs", e.target.value)
                      }
                      size="small"
                      type="tel"
                      inputMode="numeric"
                      inputProps={{ pattern: "[0-9]*" }}
                      onKeyDown={(e) => {
                        if (
                          [
                            "Backspace",
                            "Delete",
                            "Tab",
                            "Escape",
                            "Enter",
                            "ArrowLeft",
                            "ArrowRight",
                          ].includes(e.key)
                        )
                          return;
                        if (!/[0-9]/.test(e.key)) e.preventDefault();
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ width: 160 }}>
                    <TextField
                      value={row.weightPerPcs}
                      onChange={(e) =>
                        handleCellChange(index, "weightPerPcs", e.target.value)
                      }
                      size="small"
                      type="number"
                      fullWidth
                      inputProps={{ step: "any", min: 0 }}
                    />
                  </TableCell>
                  <TableCell sx={{ width: 160 }}>
                    <TextField
                      value={row.weight}
                      onChange={(e) =>
                        handleCellChange(index, "weight", e.target.value)
                      }
                      size="small"
                      type="number"
                      fullWidth
                      inputProps={{ step: "any", min: 0 }}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 180 }}>
                    {/* Type freely OR pick "Other" from the dropdown. */}
                    <Autocomplete
                      freeSolo
                      fullWidth
                      options={["Other"]}
                      inputValue={row.description || ""}
                      onInputChange={(e, val) =>
                        handleCellChange(index, "description", val)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          fullWidth
                          placeholder="Description"
                        />
                      )}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default BrandStoneGrid;
